import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { authApi, type User } from '@/api/auth'
import { AUTH_TOKEN_KEY } from '@/api/client'

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

interface AuthContextValue {
  token: string | null
  user: User | null
  status: AuthStatus
  login(token: string, user: User): void
  logout(): Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(AUTH_TOKEN_KEY))
  const [user, setUser] = useState<User | null>(null)
  const [status, setStatus] = useState<AuthStatus>(() =>
    localStorage.getItem(AUTH_TOKEN_KEY) ? 'loading' : 'unauthenticated',
  )

  // 启动时若 localStorage 有 token → 调 auto-login 续期
  useEffect(() => {
    const saved = localStorage.getItem(AUTH_TOKEN_KEY)
    if (!saved) {
      setStatus('unauthenticated')
      return
    }
    let cancelled = false
    authApi.autoLogin(saved)
      .then(res => {
        if (cancelled) return
        localStorage.setItem(AUTH_TOKEN_KEY, res.token)
        setToken(res.token)
        setUser(res.user)
        setStatus('authenticated')
      })
      .catch(() => {
        if (cancelled) return
        localStorage.removeItem(AUTH_TOKEN_KEY)
        setToken(null)
        setUser(null)
        setStatus('unauthenticated')
      })
    return () => { cancelled = true }
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    token,
    user,
    status,
    login(newToken, newUser) {
      localStorage.setItem(AUTH_TOKEN_KEY, newToken)
      setToken(newToken)
      setUser(newUser)
      setStatus('authenticated')
    },
    async logout() {
      try {
        await authApi.logout()
      } catch {
        // 即使后端登出失败也清本地，避免卡在已登录态
      }
      localStorage.removeItem(AUTH_TOKEN_KEY)
      setToken(null)
      setUser(null)
      setStatus('unauthenticated')
    },
  }), [token, user, status])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
